import { db, auth } from '../../../firebase/config';
import { 
    collection, 
    doc, 
    updateDoc, 
    arrayUnion,
    serverTimestamp,
    writeBatch,
    query,
    collectionGroup,
    where,
    getDocs
} from 'firebase/firestore';

// Utilidades del ecosistema centralizado (Pilares del Canvas)
import { parseCurrency, normalizeString } from '../../../utils/segurosUtils';
import { cleanPayload, checkEsCaucionEspecial } from './policyUtils';

// Importamos la utilidad de persistencia centralizada y auditoría
import { saveToFirestore } from '../../../firebase/cleanPayload';
import { getNombreOperador } from '../../../firebase/firebaseUtils';

// Importación del Servicio unificado de Medios de Pago (Reutilización de Lógica Senior)
import { registrarMedioPagoCliente } from '../../../utils/paymentService';

// Firebase Firestore Timestamp para registrar fechas exactas de gestión
import { Timestamp } from 'firebase/firestore';

/**
 * Registra un nuevo medio de pago (CBU o Tarjeta) delegando de forma segura
 * en nuestro servicio unificado de persistencia y validación de cobros.
 * @param {string} clientId - ID del cliente
 * @param {object} formData - Estado del formulario de pólizas
 * @returns {Promise<string>} Medio de pago formateado y registrado en la ficha del cliente
 */
const registerNewPaymentMethod = async (clientId, formData) => {
    const { 
        formaPago, nuevoBanco, nuevoCbu, 
        nuevaMarca, nuevoNumero, nuevoVto 
    } = formData;

    // Normalizamos las propiedades locales al esquema de datos esperado por el servicio unificado
    const medioData = {
        banco: nuevoBanco,
        cbu: nuevoCbu,
        numeroCbu: nuevoCbu,
        marca: nuevaMarca,
        numero: nuevoNumero,
        numeroTarjeta: nuevoNumero,
        vencimientoTarjeta: nuevoVto,
        vto: nuevoVto
    };

    // El servicio se encarga de validar el CBU/Tarjeta, guardarlo en el cliente y devolver la etiqueta formateada
    return await registrarMedioPagoCliente(clientId, medioData, formaPago);
};

/**
 * Crea el Endoso 0 (Refacturación/Cobranza inicial) para Cauciones Especiales.
 * @param {string} clientId - ID del cliente
 * @param {string} policyId - ID de la póliza creada
 * @param {object} payload - Datos de la póliza ya procesados
 * @param {string} operadorNombre - Nombre de usuario auditor
 */
const createEndoso0 = async (clientId, policyId, payload, operadorNombre) => {
    const endososPath = `public/data/clientes/${clientId}/polizas/${policyId}/endosos`;
    
    const endoso0Raw = {
        tipoGestion: 'Refacturacion', // ◄ CONSISTENCIA: Sigue el estándar unificado de tipoGestion
        tipoEndoso: payload.ref_tipoEndoso || 'Refacturación',
        numeroFactura: payload.ref_numeroFactura || '',
        numeroEndoso: payload.ref_numeroEndoso || '0',
        inicioVigencia: payload.ref_inicioVigencia || payload.inicioVigencia || '',
        finVigencia: payload.ref_finVigencia || payload.finVigencia || '', 
        moneda: payload.ref_moneda || payload.moneda || '$',
        prima: payload.ref_prima || payload.primaEmitida || 0,
        primaEmitida: payload.ref_primaEmitida || payload.primaEmitida || 0,
        estadoRefacturacion: payload.ref_estadoGestion || 'Abonado', 
        quienCobro: payload.ref_quienCobro || payload.quienCobro || '',
        fechaCobro: payload.ref_fechaCobro || payload.fechaCobro || '',
        formaDespacho: payload.ref_formaDespacho || payload.formaDespacho || '',
        fechaDespacho: payload.ref_fechaDespacho || payload.fechaDespacho || '',
        quienRenovo: payload.quienRenovo || '',
        usuarioId: auth.currentUser?.uid || 'SISTEMA',
        usuarioNombre: operadorNombre || 'SISTEMA',
        clienteId: clientId,
        clienteNombre: payload.clienteNombre || '',
        clienteApellido: payload.clienteApellido || '',
        clienteCuit: payload.clienteCuit || '',
        clienteDni: payload.clienteDni || '',
        polizaId: policyId,
        nPoliza: payload.nPoliza,
        compania: payload.compania,
        companiaId: payload.companiaId || '',
        riesgo: payload.riesgo, 
        riesgoId: payload.riesgoId || '',
        subriesgo: payload.subriesgo || '',
        canal: payload.canal || '',
        canalNombre: payload.canalNombre || '',
        canalNombreId: payload.canalNombreId || '',
        subcanalNombre: payload.subcanalNombre || '',
        subcanalNombreId: payload.subcanalNombreId || '',
        formaPago: payload.formaPago || '',
        fechaCarga: payload.fechaCarga,
    };

    await saveToFirestore(endososPath, endoso0Raw);
};

/**
 * Sincroniza cambios desde la raíz de la póliza hacia el Endoso 0 existente de Caución Especial.
 * @param {string} clientId - ID del cliente
 * @param {string} policyId - ID de la póliza editada
 * @param {object} formData - Datos del formulario
 * @param {string} operadorNombre - Nombre de usuario auditor
 */
const syncRootToEndoso0 = async (clientId, policyId, formData, operadorNombre) => {
    try {
        const endososRef = collection(db, `public/data/clientes/${clientId}/polizas/${policyId}/endosos`);
        const q = query(endososRef, where('numeroEndoso', '==', '0'));
        const snap = await getDocs(q);

        if (!snap.empty) {
            const endoso0Doc = snap.docs[0];
            const syncPayloadRaw = {
                tipoEndoso: formData.ref_tipoEndoso || 'Refacturación',
                numeroFactura: formData.ref_numeroFactura || formData.numeroFactura || '',
                inicioVigencia: formData.ref_inicioVigencia || formData.inicioVigencia || '',
                finVigencia: formData.ref_finVigencia || formData.finVigencia || '',
                moneda: formData.ref_moneda || formData.moneda || '$',
                prima: parseCurrency(formData.ref_prima),
                primaEmitida: parseCurrency(formData.ref_primaEmitida),
                estadoRefacturacion: formData.ref_estadoGestion || 'PENDIENTE',
                quienCobro: formData.ref_quienCobro || '',
                fechaCobro: formData.ref_fechaCobro || '',
                formaDespacho: formData.ref_formaDespacho || '',
                fechaDespacho: formData.ref_fechaDespacho || '',
                usuarioId: auth.currentUser?.uid || 'SISTEMA',
                usuarioNombre: operadorNombre,
                ultimaModificacion: serverTimestamp()
            };
            await updateDoc(endoso0Doc.ref, cleanPayload(syncPayloadRaw, true));
        }
    } catch (error) {
        console.error("Error en syncRootToEndoso0:", error);
    }
};

/**
 * Guarda o actualiza una póliza patrimonial en Firestore procesando postventas, renovaciones y packs.
 */
export const savePolicy = async ({
    formData,
    activeClient,
    initialData,
    mode,
    tipoCarga
}) => {
    const finalClientId = activeClient?.id || initialData?.clienteId;
    if (!finalClientId) throw new Error("No se puede guardar la póliza sin un cliente activo.");

    const operadorNombre = getNombreOperador(); // Obtiene el nombre del operador limpio y en mayúsculas automáticamente

    const isUpdate = mode === 'edit' && !!initialData?.id;
    const esCaucionEspecial = checkEsCaucionEspecial(formData.riesgo, formData.subriesgo);

    // 1. GESTIÓN DE NUEVO MEDIO DE PAGO (Si aplica)
    let medioPagoFinal = formData.medioPago;
    if (formData.medioPago === 'NEW') {
        medioPagoFinal = await registerNewPaymentMethod(finalClientId, formData);
    }

    const cleanArrayField = (arr) => (arr || []).map(v => (v || '').toUpperCase().trim()).filter(Boolean);
    
    // 2. PREPARACIÓN DE PAYLOAD (Aislamos variables de UI / Métodos de pago temporales)
    const { 
        nuevoBanco, nuevoCbu, nuevaMarca, nuevoNumero, nuevoVto, 
        patente, clienteNombreCompleto,
        fechaUltimaGestion, 
        ultimaModificacionSistema, 
        ultimaModificacion: prevUltimaMod,
        pv_fechaGestion, pv_tipoGestion, pv_quienGestiono, pv_comentarios, pv_fechaProximaGestion, 
        ...rest 
    } = formData;
    
    const patentesFinales = cleanArrayField(formData.patentes);
    const actividadesFinales = cleanArrayField(formData.actividades);
    const ubicacionesRiesgoFinales = (formData.ubicacionesRiesgo || [])
        .filter(u => u.direccion && u.direccion.trim() !== '') 
        .map(u => ({
            direccion: u.direccion.toUpperCase().trim(),
            m2: u.m2 ? Number(u.m2) : 0 
        }));
    const especialidadesFinales = cleanArrayField(formData.especialidades);

    const nombreFinal = (activeClient?.nombre || formData.clienteNombre || '').trim().toUpperCase();
    const apellidoFinal = (activeClient?.apellido || formData.clienteApellido || '').trim().toUpperCase();

    let payloadRaw = { 
        ...rest,
        type: 'patrimoniales', // Unificación estructural garantizada en base de datos
        companiaId: formData.companiaId || '', 
        riesgoId: formData.riesgoId || '', 
        medioPago: medioPagoFinal || formData.formaPago?.toUpperCase() || '',
        clienteCuit: activeClient?.cuit || formData.clienteCuit || '',
        clienteDni: activeClient?.dni || formData.clienteDni || '',
        patentes: patentesFinales.length > 0 ? patentesFinales : [''],
        actividades: actividadesFinales.length > 0 ? actividadesFinales : [''],
        ubicacionesRiesgo: ubicacionesRiesgoFinales.length > 0 ? ubicacionesRiesgoFinales : [''],
        especialidades: especialidadesFinales.length > 0 ? especialidadesFinales : [''],
        
        // Conversión segura usando parseCurrency centralizado
        primaEmitida: parseCurrency(formData.primaEmitida),
        premio: parseCurrency(formData.premio),
        primaAdelanto: parseCurrency(formData.primaAdelanto),
        primaPrimeraCuota: parseCurrency(formData.primaPrimeraCuota),
        ref_prima: parseCurrency(formData.ref_prima),
        ref_primaEmitida: parseCurrency(formData.ref_primaEmitida),
        
        comision: parseFloat(formData.comision) || 0,
        comisionOrganizador: parseFloat(formData.comisionOrganizador) || 0,
        comisionAsociado: parseFloat(formData.comisionAsociado) || 0,
        clienteId: finalClientId,
        clienteNombre: nombreFinal, // ◄ CORRECCIÓN: Quitamos el arrow function accidental para persistir string plano
        clienteApellido: apellidoFinal,
        postventa: formData.postventa || 'No'
    };

    // --- INTERCEPCIÓN POST VENTA INICIAL ---
    const createFirstPostVenta = formData.postventa === 'Si' && pv_tipoGestion;

    if (createFirstPostVenta) {
        payloadRaw.postventa_estado = 'Abierto';
        payloadRaw.postventa_numero_gestion = formData.postventa_numero_gestion || `PV-${Math.floor(10000 + Math.random() * 90000)}`;
        payloadRaw.postventa_fechaProximaGestion = pv_fechaProximaGestion || '';
        payloadRaw.postventa_quienGestiono = pv_quienGestiono || '';
        payloadRaw.postventa_ultimoTipoGestion = pv_tipoGestion || '';
    }

    // Si no es Caución Especial, removemos de forma limpia los campos prefijados con ref_
    if (!esCaucionEspecial) {
        Object.keys(payloadRaw).forEach(key => {
            if (key.startsWith('ref_')) delete payloadRaw[key];
        });
    }

    // Guardado de la póliza principal en Firestore (cleanPayload se ejecuta dentro de saveToFirestore)
    const polizaPath = `public/data/clientes/${finalClientId}/polizas`;
    const savedPoliza = await saveToFirestore(polizaPath, payloadRaw, isUpdate ? initialData.id : null);
    
    const polizaId = savedPoliza.id;

    // --- CREACIÓN DEL DOCUMENTO DE GESTIÓN POST VENTA ---
    if (createFirstPostVenta) {
        try {
            const now = new Date();
            const fechaGest = pv_fechaGestion || new Date().toISOString().split('T')[0];
            const [y, m, d] = fechaGest.split('-').map(Number);
            // Combinamos la fecha elegida con la hora exacta actual para garantizar el orden correcto en timelines
            const dateWithCurrentTime = new Date(y, m - 1, d, now.getHours(), now.getMinutes(), now.getSeconds());

            // Mapeamos con 'tipoGestion' y 'comentario' de forma nativa e inmutable
            const postVentaPayload = {
                fechaCreacion: Timestamp.fromDate(dateWithCurrentTime),
                tipoGestion: pv_tipoGestion,
                quienGestiono: pv_quienGestiono,
                comentario: pv_comentarios || '',
                fechaProximaGestion: pv_fechaProximaGestion || '',
            };
            
            const pvPath = `public/data/clientes/${finalClientId}/polizas/${polizaId}/gestiones_postventa`;
            await saveToFirestore(pvPath, postVentaPayload);
        } catch (e) {
            console.error("Error guardando postventa inicial:", e);
        }
    }

    // --- LÓGICA DE RENOVACIÓN (ACTUALIZACIÓN DE PÓLIZA EXPIRANTE) ---
    if (mode === 'renewal' && initialData?.id) {
        const oldPolizaRef = doc(db, `public/data/clientes/${finalClientId}/polizas`, initialData.id);
        await updateDoc(oldPolizaRef, {
            estadoGestionRenovacion: 'RENOVADA',
            gestionadoPor: formData.quienRenovo || null, //
            ultimaModificacion: serverTimestamp()
        });
    }

    // --- LÓGICA DE RECIPROCIDAD PARA VINCULACIÓN DE PACKS ---
    const nPolizaActual = (formData.nPoliza || '').trim().toUpperCase();
    if (formData.isPolizaPack && nPolizaActual && nPolizaActual !== 'PENDIENTE') {
        const referencias = (formData.referenciasPolizaPack || []).filter(r => r && r.length > 2);
        const packBatch = writeBatch(db);
        for (const refNro of referencias) {
            const qSibling = query(collectionGroup(db, 'polizas'), where('nPoliza', '==', refNro));
            const siblingSnap = await getDocs(qSibling);
            siblingSnap.forEach((siblingDoc) => {
                packBatch.update(siblingDoc.ref, {
                    isPolizaPack: true,
                    referenciasPolizaPack: arrayUnion(nPolizaActual),
                    ultimaModificacion: serverTimestamp()
                });
            });
        }
        await packBatch.commit();
    }

    // 3. SINCRONIZACIÓN Y PROPAGACIÓN DE ENDOSO CERO (CAUCIONES)
    if (!isUpdate) {
        if (esCaucionEspecial) await createEndoso0(finalClientId, polizaId, payloadRaw, operadorNombre);
    } else {
        if (esCaucionEspecial) await syncRootToEndoso0(finalClientId, polizaId, formData, operadorNombre);
    }

    return { docId: polizaId, payload: payloadRaw };
};